import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users, Snowflake, Pause, Sparkles, KeyRound, ArrowLeft } from 'lucide-react';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { QrSala } from '../components/QrSala';
import { useSalaViva } from '../hooks/useSalaViva';
import { Temporizador } from '../components/Temporizador';
import { IndicadorConexion } from '../components/IndicadorConexion';
import type { Presencia } from '../lib/ws';
import { primitivaDe } from '../components/primitivas/registro';
import { Barras } from '../components/primitivas/Barras';
import { vistaDe } from '../components/proyecciones/registro';
import {
  sesionApi,
  sesionKeys,
  resolverCaptura,
  normalizarOpciones,
  type ActividadDetalle,
  type Agregado,
  type Aporte,
  type BrandingResuelto,
  type RecapSesion,
} from '../lib/sesion';

/**
 * Pantalla (/pantalla/:sesionId) — proyección navy para el salón, legible a
 * 6 m, SIN controles. Muestra el AGREGADO EN VIVO por WebSocket (< 1 s): total,
 * conteos por grupo y por tipo, timer con autoridad de servidor, presencia por
 * grupo y revelación al cierre. Si el WS cae, el polling toma la posta
 * (modo degradado transparente) sin romper la proyección.
 *
 * Regla C7: NUNCA se muestran métricas por persona; solo agregados por grupo /
 * por opción, y la presencia también agregada por grupo.
 */
export function PantallaPage() {
  // Navy SIEMPRE: la proyección a 6 metros no obedece la preferencia personal.
  useSurfaceTheme('pantalla', 'dark', { respetarPreferencia: false });
  const { sesionId } = useParams<{ sesionId: string }>();

  const sala = useSalaViva(sesionId);
  // En vivo, el WS empuja; dejamos un poll lento de seguridad. Degradado -> 3 s.
  const pollAgenda = sala.enVivo ? 20_000 : 3000;

  // Invitación + branding de sala (única query; la Bienvenida la recibe por
  // props). Poll suave: el PIN puede fijarse y el branding cambiar con la
  // Pantalla ya encendida.
  const infoSala = useQuery({
    queryKey: ['sala', sesionId ?? 'nada'],
    queryFn: () => sesionApi.sala(sesionId!),
    enabled: Boolean(sesionId),
    refetchInterval: 60_000,
  });
  const branding = infoSala.data?.branding ?? null;

  const sesion = useQuery({
    queryKey: sesionId ? sesionKeys.sesion(sesionId) : ['sesion', 'nada'],
    queryFn: () => sesionApi.sesion(sesionId!),
    enabled: Boolean(sesionId),
    refetchInterval: pollAgenda,
  });

  const enVivoAct = sesion.data?.actividades.find(
    (a) => a.estado === 'abierta' || a.estado === 'pausada',
  );
  const cerradas = (sesion.data?.actividades ?? [])
    .filter((a) => a.estado === 'cerrada')
    .sort((a, b) => a.orden - b.orden);
  const focoResumen = enVivoAct ?? cerradas[cerradas.length - 1] ?? null;

  const actividad = useQuery({
    queryKey: focoResumen ? sesionKeys.actividad(focoResumen.id) : ['actividad', 'nada'],
    queryFn: () => sesionApi.actividad(focoResumen!.id),
    enabled: Boolean(focoResumen),
    refetchInterval: pollAgenda,
  });

  const act = actividad.data ?? null;
  const estaActiva = act?.estado === 'abierta' || act?.estado === 'pausada';

  // Recap de cierre (A23): con la sesión CERRADA, la Pantalla deja de mostrar
  // la última actividad y proyecta qué dejó la sala — el momento de valor.
  const sesionCerrada = sesion.data?.estado === 'cerrada';
  const recap = useQuery({
    queryKey: ['recap', sesionId ?? 'nada'],
    queryFn: () => sesionApi.recap(sesionId!),
    enabled: Boolean(sesionId) && sesionCerrada,
  });

  const aportes = useQuery({
    queryKey: act ? sesionKeys.aportes(act.id) : ['aportes', 'nada'],
    queryFn: () => sesionApi.aportes(act!.id),
    enabled: Boolean(act) && act?.estado !== 'preparada',
    // En vivo el agregado llega por WS; solo polleamos si NO estamos en vivo.
    refetchInterval: !sala.enVivo && estaActiva ? 2500 : false,
  });

  // SWITCH del facilitador (evento WS 'proyeccion' / config.proyeccion_modo):
  // 'explicar' proyecta la consigna completa sin aportes; 'tablero' el formato
  // con las participaciones. En automático (null), la Pantalla explica hasta
  // que llega el primer aporte. Cerrada la actividad, SIEMPRE tablero: el
  // resultado manda.
  const totalVivo = aportes.data?.agregado.total ?? act?.total_aportes ?? 0;
  const modoExplicar =
    Boolean(act) &&
    estaActiva &&
    (act!.proyeccion_modo ?? (totalVivo === 0 ? 'explicar' : 'tablero')) === 'explicar';

  return (
    <div className="flex min-h-screen flex-col bg-surface-base px-12 py-10">
      {/* Salida discreta a la Cabina: la proyección no tiene controles, pero
          el facilitador necesita una puerta de escape sin robar protagonismo. */}
      <Link
        to={sesion.data?.proceso_id ? `/cabina?proceso=${sesion.data.proceso_id}` : '/cabina'}
        aria-label="Volver a la Cabina del facilitador"
        className="fixed left-2 top-2 z-10 flex min-h-11 min-w-11 items-center gap-1.5 rounded-md px-3 py-2 font-sans text-sm text-content-muted opacity-30 transition-opacity hover:opacity-100 focus-visible:opacity-100"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Cabina
      </Link>
      {/* Cabecera de proyección */}
      <header className="flex items-center justify-between">
        <img src="/brand/logonegativo.png" alt="ÁGORA" className="h-12 w-auto" />
        <div className="flex items-center gap-8">
          {act && estaActiva && (
            <Temporizador
              tamano="xl"
              estado={act.estado}
              abiertaAt={act.abierta_at}
              duracionMin={act.duracion_min}
              pausaAcumuladoS={act.pausa_acumulado_s}
              offsetMs={sala.offsetServidorMs}
            />
          )}
          <IndicadorConexion estado={sala.estadoConexion} />
        </div>
      </header>

      {sesionCerrada && recap.data ? (
        <RecapDeSesion recap={recap.data} />
      ) : !act ? (
        <Bienvenida
          sesionId={sesionId}
          cargando={sesion.isLoading}
          pin={infoSala.data?.pin ?? null}
          codigo={infoSala.data?.codigo_sesion ?? sesionId ?? ''}
        />
      ) : modoExplicar ? (
        <Explicacion actividad={act} pausada={act.estado === 'pausada'} />
      ) : (
        <TableroAgregado
          actividad={act}
          agregado={aportes.data?.agregado ?? null}
          aportesCrudos={aportes.data?.aportes ?? []}
          revelado={sala.ultimoCierre?.actividadId === act.id}
        />
      )}

      {/* Presencia agregada por grupo (C7) */}
      {sala.presencia && sala.presencia.conectados_total > 0 && (
        <BarraPresencia presencia={sala.presencia} />
      )}

      {/* Branding del proceso (ADR-0003): SIEMPRE visible si hay logos. */}
      {branding && branding.logos.length > 0 && <PieBranding branding={branding} />}

      {/* QR chico persistente para quien llega tarde (sprint multi-sesión):
          en vivo la Bienvenida ya no está, así que la puerta de entrada queda
          en la esquina, encima del pie y sin tapar la proyección. */}
      {sesionId && act && (
        <div className="fixed bottom-4 right-4 z-10 flex flex-col items-center gap-1 rounded-md bg-white/95 p-2 shadow-lg">
          <QrSala
            codigoSesion={infoSala.data?.codigo_sesion ?? sesionId}
            pin={infoSala.data?.pin ?? null}
            tamano={110}
          />
          <p className="font-sans text-xs font-semibold text-navy">Sumate</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------- Recap de cierre (A23) ------------------------ */

/**
 * Lo que dejó la sala — proyección de cierre de sesión, legible a 6 metros.
 * Solo agregados y resúmenes de actas congeladas (C7): números y frases, sin
 * nombres. Es el momento de valor percibido: la gente se va viendo que su
 * participación quedó registrada.
 */
function RecapDeSesion({ recap }: { recap: RecapSesion }) {
  const conAportes = recap.actividades.filter((a) => a.total_aportes > 0);
  return (
    <main className="flex flex-1 flex-col items-center justify-center text-center">
      <p className="flex items-center gap-2 font-sans text-xl uppercase tracking-wide text-content-muted">
        <Sparkles className="h-6 w-6" aria-hidden="true" />
        Sesión cerrada — esto dejó la sala
      </p>

      <div className="mt-8 flex items-end justify-center gap-14">
        <div>
          <p className="font-display text-6xl font-bold tabular-nums text-content">
            {recap.total_aportes}
          </p>
          <p className="font-sans text-xl uppercase tracking-wide text-content-muted">
            {recap.total_aportes === 1 ? 'aporte' : 'aportes'}
          </p>
        </div>
        <div>
          <p className="font-display text-6xl font-bold tabular-nums text-content">
            {recap.total_participantes}
          </p>
          <p className="font-sans text-xl uppercase tracking-wide text-content-muted">
            {recap.total_participantes === 1 ? 'persona' : 'personas'}
          </p>
        </div>
      </div>

      <div className="mt-10 grid w-full max-w-6xl gap-4 text-left sm:grid-cols-2 lg:grid-cols-3">
        {conAportes.map((a) => (
          <section
            key={a.id}
            className="rounded-lg border border-border bg-surface-raised px-5 py-4"
          >
            <h2 className="font-display text-xl font-semibold text-content">
              {a.titulo}
            </h2>
            <p className="mt-0.5 font-sans text-base text-content-muted">
              {a.total_aportes} {a.total_aportes === 1 ? 'aporte' : 'aportes'}
              {a.version_acta != null && ' · acta congelada'}
            </p>
            {a.destacados.length > 0 && (
              <ul className="mt-2 flex flex-col gap-1">
                {a.destacados.map((d) => (
                  <li
                    key={d}
                    className="truncate font-sans text-base text-content"
                    title={d}
                  >
                    «{d}»
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <p className="mt-10 max-w-3xl font-sans text-xl text-content-muted">
        Cada aporte quedó en el acta con su evidencia: la propuesta final va a
        citar lo que esta sala produjo.
      </p>
    </main>
  );
}

/* --------------------------- Pie de branding ---------------------------- */

/**
 * Pie de proyección con los logos resueltos por la cascada instancia→org→
 * proceso (ADR-0003). JAMÁS hardcodeados: salen de la config — el siguiente
 * tenant solo cambia la suya. Institución a la izquierda, cooperante a la
 * derecha, otras etiquetas al centro; la atribución («Con el apoyo de…»)
 * centrada en texto discreto.
 */
function PieBranding({ branding }: { branding: BrandingResuelto }) {
  const izquierda = branding.logos.filter((l) => l.etiqueta === 'institucion');
  const derecha = branding.logos.filter((l) => l.etiqueta === 'cooperante');
  const centro = branding.logos.filter(
    (l) => l.etiqueta !== 'institucion' && l.etiqueta !== 'cooperante',
  );

  return (
    <footer className="mt-8 flex min-h-16 items-center justify-between gap-8">
      <GrupoLogos logos={izquierda} />
      <div className="flex flex-1 flex-col items-center gap-2">
        <GrupoLogos logos={centro} />
        {branding.atribucion && (
          <p className="text-center font-sans text-sm text-content-muted">
            {branding.atribucion}
          </p>
        )}
      </div>
      <GrupoLogos logos={derecha} />
    </footer>
  );
}

function GrupoLogos({ logos }: { logos: BrandingResuelto['logos'] }) {
  if (!logos.length) return null;
  return (
    <div className="flex items-center gap-4">
      {logos.map((l) => (
        // Los SVG institucionales van A COLOR sobre el navy (sin filtros):
        // placa clara para un contraste digno — es un logo, no un adorno.
        <span key={l.url} className="rounded bg-white/95 px-3 py-1">
          <img src={l.url} alt={l.etiqueta} className="h-12 w-auto" />
        </span>
      ))}
    </div>
  );
}

/* --------------------------- Explicación ------------------------------- */

/**
 * Modo EXPLICAR: la consigna completa de la herramienta (XIA), sin aportes.
 * Es el momento en que el facilitador da la instrucción a la sala; el QR
 * chico de la esquina sigue ahí para quien va entrando. Al pasar a
 * «Tablero» (switch de la Cabina o primer aporte en automático), la
 * proyección cambia al formato con las participaciones.
 */
function Explicacion({
  actividad,
  pausada,
}: {
  actividad: ActividadDetalle;
  pausada: boolean;
}) {
  const xia = actividad.herramienta.xia;
  return (
    <main className="flex flex-1 flex-col items-center justify-center text-center">
      <p className="font-display text-2xl font-semibold uppercase tracking-wide text-cyan">
        {actividad.titulo ?? actividad.herramienta.nombre}
      </p>
      <h1 className="mt-4 max-w-5xl font-display text-5xl font-bold leading-tight text-content">
        {xia.que}
      </h1>

      {pausada && (
        <p className="mt-5 flex items-center gap-2 font-sans text-xl text-warning">
          <Pause className="h-6 w-6" aria-hidden="true" />
          En pausa — el facilitador reanuda en un momento
        </p>
      )}

      <div className="mt-12 grid w-full max-w-5xl gap-6 text-left sm:grid-cols-2">
        <section className="rounded-lg border border-border bg-surface-raised px-6 py-5">
          <h2 className="font-sans text-lg uppercase tracking-wide text-content-muted">
            Por qué lo hacemos
          </h2>
          <p className="mt-2 font-sans text-2xl leading-snug text-content">{xia.por_que}</p>
        </section>
        <section className="rounded-lg border border-border bg-surface-raised px-6 py-5">
          <h2 className="font-sans text-lg uppercase tracking-wide text-content-muted">
            Qué va a quedar
          </h2>
          <p className="mt-2 font-sans text-2xl leading-snug text-content">{xia.hacia}</p>
        </section>
      </div>

      <p className="mt-12 max-w-3xl font-sans text-2xl text-content-muted">
        Participá desde tu teléfono en{' '}
        <span className="font-semibold text-content">agora.inteligencia.com.gt</span>
      </p>
    </main>
  );
}

/* ---------------------------- Tablero ---------------------------------- */

function TableroAgregado({
  actividad,
  agregado,
  aportesCrudos,
  revelado,
}: {
  actividad: ActividadDetalle;
  agregado: Agregado | null;
  aportesCrudos: Aporte[];
  revelado: boolean;
}) {
  // Resolución de proyección (D5c → D5b → genérico, nunca en blanco):
  // 1) la VISTA que declara el manifiesto (registro de vistas),
  // 2) la proyección de la primitiva (registro de primitivas),
  // 3) los agregados genéricos de abajo (total + por grupo).
  const res = resolverCaptura(actividad.herramienta);
  // Momento informativo (cápsula/lectura): sin captura DELIBERADAMENTE — la
  // invitación a aportar desde el teléfono no aplica; XIA + timer bastan.
  const informativa = !res.soportada && res.informativa === true;
  const proy = actividad.herramienta.proyeccion;
  const proyObj = typeof proy === 'object' && proy != null ? proy : null;
  const Vista = vistaDe(proyObj?.vista);
  const Fallback = res.soportada ? primitivaDe(res.captura.tipo).Proyeccion : null;
  // VOZ DENTRO DEL FORMATO: un audio con transcripción se proyecta como el
  // texto de su casilla — la nota dicha con categoría cae en su cuadrante,
  // no en una bitácora aparte (el audio original sigue citable en el acta).
  // El campo que llena la transcripción es el de SU sección (payload.seccion,
  // wizard ADR-0005 §2): en «espero/temo», la voz del paso «temo» es `temo`.
  const capturaDecl = res.soportada ? res.captura : null;
  const campoDeVoz = (seccion: unknown): string => {
    if (!capturaDecl) return 'texto';
    const campos = capturaDecl.campos;
    let nombres = Object.keys(campos);
    if (typeof seccion === 'string') {
      const s = capturaDecl.secciones.find((x) => x.id === seccion);
      if (s) nombres = s.campos.filter((n) => campos[n]);
    }
    if (nombres.length === 0 || nombres.includes('texto')) return 'texto';
    return (
      nombres.find(
        (n) => campos[n].tipo === 'string' && !normalizarOpciones(campos[n].opciones).length,
      ) ?? 'texto'
    );
  };

  // Moderación (M2): lo oculto por el facilitador no se proyecta jamás.
  // Lo más reciente PRIMERO (pedido del owner): en la galería proyectada, lo
  // que acaba de llegar entra arriba — igual que en el feed de la Cabina.
  const visibles = aportesCrudos
    .filter((a) => !a.oculto)
    .map((a) => {
      if (a.tipo !== 'audio' || !a.transcripcion?.texto) return a;
      // Una transcripción FALLIDA queda registrada en el acta, pero jamás se
      // proyecta como si fuera lo que la persona dijo.
      if (a.transcripcion.texto.startsWith('(transcripción falló')) return a;
      const clave = campoDeVoz(a.payload?.seccion);
      if (typeof a.payload?.[clave] === 'string') return a;
      return { ...a, payload: { ...a.payload, [clave]: a.transcripcion.texto } };
    })
    .reverse();
  const total = agregado?.total ?? actividad.total_aportes ?? 0;
  const hablado = agregado?.hablado ?? null;
  const cerrada = actividad.estado === 'cerrada';
  const pausada = actividad.estado === 'pausada';

  return (
    <main className="flex flex-1 flex-col items-center justify-center text-center">
      <p className="font-display text-2xl font-semibold uppercase tracking-wide text-cyan">
        {actividad.titulo ?? actividad.herramienta.nombre}
      </p>
      <h1 className="mt-3 max-w-5xl font-display text-4xl font-bold leading-tight text-content">
        {actividad.herramienta.xia.que}
      </h1>

      {pausada && (
        <p className="mt-4 flex items-center gap-2 font-sans text-xl text-warning">
          <Pause className="h-6 w-6" aria-hidden="true" />
          En pausa — el facilitador reanuda en un momento
        </p>
      )}

      {cerrada && (
        <p
          className={`mt-4 flex items-center gap-2 font-sans text-xl text-success ${
            revelado ? 'animate-pulse' : ''
          }`}
        >
          {revelado ? (
            <Sparkles className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Snowflake className="h-6 w-6" aria-hidden="true" />
          )}
          {revelado ? 'Acta revelada — resultado final' : 'Acta congelada — resultado final'}
        </p>
      )}

      {/* Los DOS canales, cada uno con su número: lo enviado por el botón
          (participación con autoría) y lo captado por el micrófono de sala
          (soporte, sin autoría). Nunca se suman — se cruzan. */}
      <div className="mt-10 flex items-end justify-center gap-12">
        <div className="flex flex-col items-center">
          <p className="font-display text-4xl font-bold tabular-nums text-content">{total}</p>
          <p className="font-sans text-xl uppercase tracking-wide text-content-muted">
            {total === 1 ? 'aporte' : 'aportes'} de la sala
          </p>
        </div>
        {hablado && hablado.intervenciones > 0 && (
          <div className="flex flex-col items-center">
            <p className="font-display text-4xl font-bold tabular-nums text-violet">
              {hablado.intervenciones}
            </p>
            <p className="font-sans text-xl uppercase tracking-wide text-content-muted">
              {hablado.intervenciones === 1 ? 'intervención hablada' : 'intervenciones habladas'}
              {hablado.hablantes > 0 && ` · ${hablado.hablantes} voces`}
            </p>
          </div>
        )}
      </div>

      {/* Proyección: vista del manifiesto (D5c) o la de la primitiva (D5b).
          `revelar_al_cierre` (anti-anclaje en divergencia): mientras la
          actividad corre solo se proyecta el conteo; al congelar el acta,
          los aportes aparecen — nada de lo dicho se queda sin mostrar. */}
      {proyObj?.opciones?.revelar_al_cierre === true && !cerrada ? (
        total > 0 && (
          <p className="mt-10 max-w-3xl font-sans text-xl text-content-muted">
            Los aportes se revelan al cierre de la actividad
          </p>
        )
      ) : Vista ? (
        <Vista
          aportes={visibles}
          total={total}
          porGrupo={agregado?.por_grupo ?? []}
          opciones={proyObj?.opciones ?? {}}
        />
      ) : (
        Fallback && <Fallback aportes={visibles} total={total} />
      )}

      {agregado?.por_grupo?.length ? (
        <Barras
          titulo="Por grupo"
          filas={agregado.por_grupo.map((g) => ({ etiqueta: g.grupo, n: g.n }))}
          total={total}
        />
      ) : null}

      {total === 0 && !cerrada && !informativa && (
        <p className="mt-10 max-w-3xl font-sans text-2xl text-content-muted">
          Esperando los primeros aportes de la sala… ingresá desde tu teléfono en{' '}
          <span className="font-semibold text-content">agora.inteligencia.com.gt</span>
        </p>
      )}
    </main>
  );
}

/* --------------------------- Presencia --------------------------------- */

function BarraPresencia({ presencia }: { presencia: Presencia }) {
  return (
    <footer className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 border-t border-border pt-6">
      <span className="flex items-center gap-2 font-display text-2xl font-semibold text-content">
        <Users className="h-7 w-7 text-cyan" aria-hidden="true" />
        {presencia.conectados_total} conectados
      </span>
      {presencia.por_grupo.map((g) => (
        <span key={g.grupo} className="font-sans text-xl text-content-muted">
          {g.grupo}: <span className="font-semibold tabular-nums text-content">{g.n}</span>
        </span>
      ))}
    </footer>
  );
}

/* --------------------------- Bienvenida -------------------------------- */

function Bienvenida({
  sesionId,
  cargando,
  pin,
  codigo,
}: {
  sesionId: string | undefined;
  cargando: boolean;
  /** PIN COMPARTIDO del proceso: dato público de proyección (query en la raíz). */
  pin: string | null;
  codigo: string;
}) {
  return (
    // Partida en dos (pedido del owner): mensaje a la izquierda, QR GRANDE a la
    // derecha — SIEMPRE, haya o no PIN (sin PIN el QR lleva solo ?sala= y la
    // persona entra con Google o su PIN personal). En angosto se apila.
    <main className="flex flex-1 flex-col items-center justify-center gap-x-20 gap-y-12 text-center lg:flex-row lg:text-left">
      <div className="flex max-w-2xl flex-col items-center lg:items-start">
        <h1 className="font-display text-4xl font-bold leading-tight text-content">
          {cargando ? 'Preparando la sesión…' : 'La sesión está por comenzar'}
        </h1>
        <p className="mt-6 font-sans text-2xl text-content-muted">
          Ingresá desde tu teléfono en{' '}
          <span className="font-semibold text-content">agora.inteligencia.com.gt</span>
        </p>
        <p className="mt-4 font-sans text-2xl text-content-muted">
          Código de sala:{' '}
          <span className="font-semibold tabular-nums text-content">{codigo}</span>
        </p>
        {pin && (
          <p className="mt-8 flex items-center gap-5 font-display font-bold text-cyan">
            <KeyRound className="h-14 w-14" aria-hidden="true" />
            <span className="text-7xl tabular-nums tracking-widest">PIN: {pin}</span>
          </p>
        )}
        <p className="mt-10 flex items-center gap-3 font-sans text-xl text-content-muted">
          <Users className="h-7 w-7" aria-hidden="true" />
          Esperando a que el facilitador abra la primera actividad
        </p>
        <p className="mt-10 font-sans text-lg text-content-muted tabular-nums">
          Sesión {sesionId}
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-center gap-4">
        <div className="rounded-lg bg-white p-3">
          <QrSala codigoSesion={codigo} pin={pin} tamano={260} />
        </div>
        <p className="font-sans text-2xl font-semibold text-content">Escaneá para entrar</p>
        <p className="max-w-xs text-center font-sans text-xl text-content-muted">
          {pin
            ? `con tu nombre y el PIN ${pin}`
            : 'con tu cuenta de Google o tu PIN personal'}
        </p>
      </div>
    </main>
  );
}
